/**
 * 认证工具模块
 * 
 * @module utils/auth
 * @description 提供认证相关的工具函数，包括Token验证、过期处理等
 * @author 汪浩
 * @copyright 武汉职创未来科技有限公司
 */

import { ElMessage } from 'element-plus'
import router from '@/router'
import { ERROR_CODES } from '@/api/errorCodes'

/**
 * 需要重定向到登录页的错误码列表
 */
const REDIRECT_ERROR_CODES: number[] = [
  ERROR_CODES.TOKEN_EXPIRED,    // Token过期
  ERROR_CODES.ACCOUNT_DISABLED  // 账号已禁用
]

/**
 * 检查是否需要重定向到登录页
 * 
 * @param errorCode - 业务错误码
 * @returns 是否需要重定向
 */
export const shouldRedirectToLogin = (errorCode: number): boolean => {
  return REDIRECT_ERROR_CODES.includes(errorCode)
}

/**
 * 处理认证失败，清除本地存储并跳转到登录页
 * 
 * @param message - 可选的提示消息
 * 
 * @example
 * ```typescript
 * // 在API响应拦截器中调用
 * if (response.data.code === ERROR_CODES.TOKEN_EXPIRED) {
 *   handleAuthFailure('登录已过期，请重新登录')
 * }
 * ```
 */
export const handleAuthFailure = (message?: string): void => {
  // 显示提示消息
  if (message) {
    ElMessage.warning(message)
  }
  
  // 清除本地存储的认证信息
  clearAuthInfo()
  
  // 跳转到登录页
  router.push('/login')
}

/**
 * 检查Token是否过期
 * 
 * @description 检查tokenExpireTime是否存在且未过期
 * @returns Token是否过期
 */
export const isTokenExpired = (): boolean => {
  const expireTime = localStorage.getItem('tokenExpireTime')
  
  // 如果没有设置过期时间，说明用户没有勾选"记住登录"
  // 此时 token 存储在 sessionStorage 中，在当前会话中有效
  if (!expireTime) {
    return false
  }
  
  // 检查当前时间是否超过过期时间
  const now = Date.now()
  return now > parseInt(expireTime, 10)
}

/**
 * 获取存储的Token
 * 
 * @description 从 localStorage 或 sessionStorage 中获取 token
 * @returns Token 或 null
 */
export const getToken = (): string | null => {
  return localStorage.getItem('token') || sessionStorage.getItem('token')
}

/**
 * 获取存储的用户信息
 * 
 * @description 从 localStorage 或 sessionStorage 中获取用户信息
 * @returns 用户信息或 null
 */
export const getUserInfo = (): string | null => {
  return localStorage.getItem('userInfo') || sessionStorage.getItem('userInfo')
}

/**
 * 清除所有认证信息
 *
 * @description 清除 localStorage 和 sessionStorage 中的认证信息
 */
export const clearAuthInfo = (): void => {
  localStorage.removeItem('token')
  localStorage.removeItem('userInfo')
  localStorage.removeItem('tokenExpireTime')
  localStorage.removeItem('login_ip')
  localStorage.removeItem('login_location')
  sessionStorage.removeItem('token')
  sessionStorage.removeItem('userInfo')
  sessionStorage.removeItem('login_ip')
  sessionStorage.removeItem('login_location')
}

/**
 * 保存客户端IP地址
 *
 * @param ip - IP地址
 * @param remember - 是否记住登录
 */
export const saveClientIP = (ip: string, remember: boolean = false): void => {
  if (remember) {
    localStorage.setItem('clientIP', ip)
  } else {
    sessionStorage.setItem('clientIP', ip)
  }
}

/**
 * 获取保存的客户端IP地址
 *
 * @returns IP地址或 null
 */
export const getClientIPFromStorage = (): string | null => {
  return localStorage.getItem('clientIP') || sessionStorage.getItem('clientIP')
}

/**
 * 验证用户认证状态
 * 
 * @description 检查是否有token且token未过期
 * @returns 用户是否已认证
 */
export const isAuthenticated = (): boolean => {
  const token = getToken()
  
  // 没有token，未认证
  if (!token) {
    return false
  }
  
  // 检查token是否过期
  if (isTokenExpired()) {
    // Token已过期，清除认证信息
    handleAuthFailure('登录已过期，请重新登录')
    return false
  }
  
  return true
}
